import _ from 'lodash'
import { computed, effect, Injectable, Injector, signal, untracked } from '@angular/core'

import { TClaimName } from 'src/assets/common/enums/claim-name.g'
import { IUserClaimData } from './models/user-claim-data'
import { IOwnerClaimsData } from 'src/app/infrastructure/models/response-data'
import { LogService } from 'src/app/infrastructure/services/log.service'
import { IRouteKeyData } from '../route-service/models/route-key-data'
import { ClaimPage } from '../../models/claim-page'
import { domainUserDto, IDomainUserDto } from 'src/assets/common/models/domain-user-dto.g'
import { IProjectMemberDto, projectMemberDto } from 'src/assets/common/models/project-member-dto.g'
import { IUserDto, userDto } from 'src/assets/common/models/user-dto.g'
import { domainUserData, IDomainUserData } from 'src/app/features/domain-user/models/domain-user-data'
import { IProjectMemberData, projectMemberData } from 'src/app/features/project-member/models/project-member-data'
import { IUserData, userData } from 'src/app/features/user/models/user-data'
import { API_SUPPRESS_ERROR_HEADER, API_SUPPRESS_PROGRESS_HEADER, API_SUPPRESS_STATE_APPLY_HEADER, ApiService } from '../api-service/api.service'
import { AuthService } from 'src/app/auth/services/auth.service'
import { parseJwt } from 'src/app/features/shared/storage/functions/parse-jwt'
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function'
import { NotificationService } from 'src/app/features/shared/notification/notification.service'
import { toAppError } from 'src/app/features/shared/error/app-error'
import { errorName } from 'src/assets/common/enums/error-name.g'

const ADMIN_PROBE_RETRY_MIN_MS = 5_000;
// Tooltip voor een knop waarvan het recht er is, maar die op deze omgeving vergrendeld is (zie claim_locked).
export const structureLockedTooltip = 'locked on this environment (editable on localhost only)';
const ADMIN_PROBE_RETRY_MAX_MS = 5 * 60_000;

@Injectable({
    providedIn: 'root'
})
export class ApplicationUserService
{
    // Lazy via Injector i.p.v. injectRequired(ApiService): ApiService injecteert zelf
    // ResponseStateService, die op zijn beurt ApplicationUserService injecteert. Eager injecteren
    // hier zou die cirkel tijdens constructie sluiten (NG0200). Pas opvragen bij eerste gebruik
    // (bv. de AdminPanel-probe in de effect hieronder) ligt ver na de constructorfase van alle drie, dus geen cyclus.
    private readonly __injector = injectRequired(Injector);
    private get __apiService(): ApiService {
        return this.__injector.get(ApiService);
    }
    // AuthService hangt zelf niet van ApplicationUserService (of iets dat daarvan afhangt) af —
    // eager injecteren hier sluit dus geen cirkel, in tegenstelling tot ApiService hierboven.
    private readonly __authService = injectRequired(AuthService);
    // NotificationService hangt alleen van MatSnackBar/NgZone af — eager injecteren sluit geen cirkel.
    private readonly __notificationService = injectRequired(NotificationService);

    // Stabiele, niet-pagina-gescoped admin-toegangscheck (zie isAdminPanel$$ hieronder).
    private readonly __isAdminPanel$$ = signal<boolean | null>(null);
    // true/false zodra de probe is teruggekomen voor de HUIDIGE ingelogde user; null = nog
    // onbekend (geen token, of probe nog in flight) — een consument die hier "is admin" op
    // baseert moet null als "nee" behandelen (fail-closed), niet als "nog niet zeker dus ja".
    readonly isAdminPanel$$ = this.__isAdminPanel$$.asReadonly();
    private __isAdminPanelCheckedFor: string | null | undefined = undefined;
    private __isAdminPanelRetryTimer: ReturnType<typeof setTimeout> | null = null;
    // Guid van het systeemdomain (domain 0, "AdminPanel") uit dezelfde probe-response — o.a. voor de
    // navigatiegeschiedenis, die domain 0 nooit als gebruikersdomain mag tonen. null = (nog) onbekend.
    private readonly __systemDomainGuid$$ = signal<string | null>(null);
    readonly systemDomainGuid$$ = this.__systemDomainGuid$$.asReadonly();
    // Volgnummer per verstuurde probe: alleen het antwoord van de LAATSTE telt. De guid-check alleen
    // ving niet af dat dezelfde user twee probes tegelijk in flight had (token kort null en weer terug
    // rond login/opstart) — beide antwoorden werden verwerkt, dus de 2FA-melding verscheen twee keer.
    private __isAdminPanelProbeSeq = 0;

    // Opnieuw proben voor dezelfde user — nodig ná het inschakelen van 2FA, zodat een AdminPanel dat
    // eerder op TwoFactorRequired strandde meteen in de drawer verschijnt i.p.v. pas na opnieuw inloggen.
    isAdminPanel_refresh(): void {
        this.__isAdminPanelCheckedFor = undefined;
        this.__isAdminPanel_probe();
    }
    private __isAdminPanelRetryDelay = ADMIN_PROBE_RETRY_MIN_MS;

    // Eenmalig per ingelogde user (userGuid uit het token). Alleen 401/403 is een definitief
    // "geen admin"; elke andere fout (netwerk, 5xx, backend die net herstart, trage DB) zegt niets
    // over de rechten en wordt met oplopende wachttijd opnieuw geprobeerd — vóór deze retry bleef
    // één mislukte probe de hele sessie (tot logout/F5) "geen admin" opleveren, zichtbaar als een
    // AdminPanel dat "soms" uit de drawer verdween.
    private __isAdminPanel_probe(): void {
        const __token = this.__authService.token$$();
        const __guid = __token ? parseJwt(__token)?.nameid ?? null : null;

        if (this.__isAdminPanelCheckedFor === __guid) return;
        this.__isAdminPanelCheckedFor = __guid;

        if (this.__isAdminPanelRetryTimer) clearTimeout(this.__isAdminPanelRetryTimer);
        this.__isAdminPanelRetryTimer = null;

        if (!__guid) {
            this.__isAdminPanel$$.set(null);
            this.__structureLocked$$.set([]);
            this.__isAdminPanelRetryDelay = ADMIN_PROBE_RETRY_MIN_MS;
            return;
        }

        // Geen set(null) hier: een guid-wissel loopt altijd via logout (token → null, hierboven al
        // op null gezet), en bij een retry voor dezelfde user blijft de laatst bekende waarde staan.

        const __retry = () => {
            if (this.__isAdminPanelCheckedFor !== __guid) return;
            this.__isAdminPanelCheckedFor = undefined;
            this.__isAdminPanelRetryTimer = setTimeout(() => this.__isAdminPanel_probe(), this.__isAdminPanelRetryDelay);
            this.__isAdminPanelRetryDelay = Math.min(this.__isAdminPanelRetryDelay * 2, ADMIN_PROBE_RETRY_MAX_MS);
        };

        // Een 403 hier is een VERWACHTE, geen foute uitkomst (de meeste ingelogde users zijn
        // geen administrator) — X_SUPPRESS_ERROR_HEADER voorkomt dat ApiService's middleware
        // 'm alsnog als gebruikersnotificatie toont (de backend zet showInUI:true op
        // missingAuthorization, terecht voor een échte, door de user geïnitieerde navigatie,
        // maar niet voor deze achtergrond-probe).
        const __seq = ++this.__isAdminPanelProbeSeq;
        void this.__apiService.GET('/api/AdminPanel/details', {
            headers: { [API_SUPPRESS_ERROR_HEADER]: 'true', [API_SUPPRESS_STATE_APPLY_HEADER]: 'true', [API_SUPPRESS_PROGRESS_HEADER]: 'true' },
        })
            .then(({ data, error, response }) => {
                // Inmiddels andere user (of uitgelogd), of een nieuwere probe: dit antwoord hoort niet
                // meer bij de huidige.
                if (this.__isAdminPanelCheckedFor !== __guid || __seq !== this.__isAdminPanelProbeSeq) return;

                if (!error) {
                    this.__systemDomainGuid$$.set((data?.modelDto as { guid?: string } | undefined)?.guid ?? null);
                    this.__isAdminPanel$$.set(true);
                    this.__structureLocked_load(__guid);
                    this.__isAdminPanelRetryDelay = ADMIN_PROBE_RETRY_MIN_MS;
                    return;
                }
                if (response.status === 401 || response.status === 403) {
                    this.__isAdminPanel$$.set(false);
                    // TwoFactorRequired komt pas ná de lidmaatschapscheck van domain 0 (een gewone user
                    // krijgt eerder al UnAuthorized): deze user heeft wél AdminPanel-toegang, maar 2FA
                    // staat uit terwijl domain 0 het vereist. De probe-fout is bewust onderdrukt, dus
                    // anders verdwijnt het AdminPanel zonder uitleg uit de drawer.
                    if (toAppError(error).name === errorName.twoFactorRequired) {
                        this.__notificationService.pushNotification({
                            severity: 'warning',
                            title: 'Tweestapsverificatie vereist',
                            text: 'Je hebt toegang tot het AdminPanel, maar dat vereist tweestapsverificatie. Schakel die in via Instellingen.',
                            duration: 0,
                        });
                    }
                    this.__isAdminPanelRetryDelay = ADMIN_PROBE_RETRY_MIN_MS;
                    return;
                }
                __retry();
            })
            .catch(() => { if (__seq === this.__isAdminPanelProbeSeq) __retry(); });
    }

    // Structuurclaims (AdminPanel: Pages/Tabs/Claims/PageComponents/ClaimActions/PageActivities) die de
    // gebruiker wél heeft, maar die de backend buiten develop wegfiltert (SecurityOptions.StructureEditable,
    // UserClaimService.StructureLocked_getDto). Alleen voor de weergave: knoppen met zo'n claim tonen
    // disabled i.p.v. verborgen, zodat het niet lijkt alsof het recht ontbreekt. Geeft nooit toegang.
    // Scope-loos (geen dGuid/oGuid): structuurpagina's bestaan alleen in het AdminPanel.
    private readonly __structureLocked$$ = signal<IUserClaimData[]>([]);

    private __structureLocked_load(guid: string): void {
        void this.__apiService.GET('/api/UserClaim/structure-locked', {
            headers: { [API_SUPPRESS_ERROR_HEADER]: 'true', [API_SUPPRESS_STATE_APPLY_HEADER]: 'true', [API_SUPPRESS_PROGRESS_HEADER]: 'true' },
        })
            .then(({ data, error }) => {
                if (this.__isAdminPanelCheckedFor !== guid) return;
                this.__structureLocked$$.set(error ? [] : (data as IUserClaimData[] | undefined) ?? []);
            })
            .catch(() => this.__structureLocked$$.set([]));
    }

    // true = de gebruiker heeft deze claim, maar hij is op deze omgeving vergrendeld (zie hierboven).
    // Alleen gebruiken nadat claim_check false gaf, om "verbergen" te vervangen door "disabled tonen".
    claim_locked(componentName: ClaimPage | null, claim: TClaimName | null): boolean {
        if (!componentName || !claim) {return false}

        return this.__structureLocked$$().some(userClaim =>
            userClaim.pageName.toLowerCase() == componentName.toLowerCase()
            && userClaim.claim.toLowerCase() == claim.toLowerCase()
        )
    }

    // Zelfde patroon als isAdminPanel$$ hierboven, maar voor het e-mailadres van de ingelogde user
    // t.b.v. de header-account-cluster: eenmalig geladen per ingelogde user (userGuid uit het token),
    // null zolang onbekend/in-flight. accountEmail_refresh() forceert een herlaad — nodig ná een
    // geslaagde e-mailwijziging, zodat de header niet het oude adres blijft tonen tot de eerstvolgende
    // login.
    private readonly __accountEmail$$ = signal<string | null>(null);
    readonly accountEmail$$ = this.__accountEmail$$.asReadonly();
    private __accountEmailCheckedFor: string | null | undefined = undefined;

    accountEmail_refresh(): void {
        this.__accountEmailCheckedFor = undefined;
        this.__accountEmail_probe();
    }

    private __accountEmail_probe(): void {
        const __token = this.__authService.token$$();
        const __guid = __token ? parseJwt(__token)?.nameid ?? null : null;

        if (this.__accountEmailCheckedFor === __guid) return;
        this.__accountEmailCheckedFor = __guid;

        if (!__guid) { this.__accountEmail$$.set(null); return; }

        this.__accountEmail$$.set(null);
        void this.__apiService.GET('/api/Account/details', {
            headers: { [API_SUPPRESS_ERROR_HEADER]: 'true', [API_SUPPRESS_STATE_APPLY_HEADER]: 'true', [API_SUPPRESS_PROGRESS_HEADER]: 'true' },
        })
            .then(({ data, error }) => this.__accountEmail$$.set(error ? null : data?.modelDto?.[userDto.email] ?? null))
            .catch(() => this.__accountEmail$$.set(null));
    }

    constructor() {
        // UserClaims (zie claim_check/claims_new hieronder) is NIET bruikbaar voor "is deze user
        // administrator, ongeacht welke pagina nu open staat": dat veld wordt door ELK response
        // volledig VERVANGEN en gestempeld met de dGuid/oGuid van de pagina die het response
        // veroorzaakte (zie claims_new) — een globaal (dGuid=null) geschaalde admin-claim staat er
        // dus alleen in vlak ná een bezoek aan Home/AdminPanel zelf. Vandaar een APARTE, dedicated
        // probe — zelfde /api/AdminPanel/details-endpoint dat de echte AdminPanel-pagina al gebruikt
        // (RequiresAuthContext + claim-check server-side, dus authoritatief) — eenmalig per
        // ingelogde user (userGuid uit het token), niet per pagina-bezoek. Zelfde patroon als
        // __ownerClaims$$ hieronder.
        effect(() => {
            this.__authService.token$$();
            untracked(() => this.__isAdminPanel_probe());
        });

        effect(() => {
            this.__authService.token$$();
            untracked(() => this.__accountEmail_probe());
        });
    }

    domainUserData: IDomainUserData | null = null;
    projectMemberData: IProjectMemberData | null = null;
    // Ingelogde User (record-owner-identiteit, los van de claim-scope DomainUser/ProjectMember).
    // Buiten een domein (bv. Home) komt deze uit de response; binnen domein/project is hij ook
    // afleidbaar (zie UserGuid).
    userData: IUserData | null = null;

    // User.Guid voor de owner-check: het JWT (nameid-claim) is de ENIGE betrouwbare, race-vrije bron
    // — userData/domainUserData/projectMemberData zijn alledrie gedeelde, sessie-brede velden die
    // door ELK response worden overschreven (zie ResponseStateService.apply), dus konden op het
    // moment van een owner_check al de identiteit van een ANDER, ondertussen binnengekomen response
    // dragen i.p.v. de daadwerkelijk ingelogde gebruiker — precies de oorzaak van een tab die zich
    // ten onrechte als "eigen record" gedroeg (DomainUserPhotos, zie
    // [[project_domainuserphotos_scope_bug]]). Het token zelf leeft alleen in het geheugen van AuthService
    // en wordt nooit door een response overschreven, dus nameid is hier altijd de daadwerkelijk
    // ingelogde gebruiker. De response-afgeleide velden blijven staan als terugval voor het
    // (theoretische) geval dat het token geen nameid-claim draagt.
    get UserGuid(): string | null {
        const __token = this.__authService.token$$();
        const __tokenGuid = __token ? parseJwt(__token)?.nameid ?? null : null;

        return __tokenGuid
            ?? this.userData?.[userData.guid]
            ?? this.domainUserData?.[domainUserData.user_guid]
            ?? this.projectMemberData?.[projectMemberData.user_guid]
            ?? null;
    }

    // Record-owner-check: maker (User.Guid) == ingelogde User. Centrale FE-owner-bepaling zodat
    // owner-override via het userClaim-autorisatiepad meeloopt i.p.v. via een server-UI-vlag.
    owner_check(createdByGuid: string | null | undefined): boolean {
        const __userGuid = this.UserGuid;
        return !!__userGuid && !!createdByGuid && __userGuid === createdByGuid;
    }

    // Owner-set (RecordOwner/EntityOwner) komt mee in elke response (ownerClaims, zie ResponseStateService.apply
    // en backend OwnerClaimsResultFilter), gefilterd op de DomainUser van die response. Bewust GEEN onderdeel
    // van userClaims$$/claim_check: eigenaarschap is instantie-gebonden (dit specifieke record), dus zou anders
    // voor élk record "waar" worden. Opgeslagen per domain-scope (zelfde sleutel als claims_add).
    private __ownerClaimsByScope$$ = signal<Record<string, IOwnerClaimsData>>({});
    // Laatst ontvangen owner-set, als terugval voor een scope waarvoor nog niets binnen is. Dat voorkomt dat
    // de tab-auth blijft wachten (zie TabListService, point-of-no-return op pageMode). Server blijft leidend.
    private __ownerClaimsLast$$ = signal<IOwnerClaimsData | null>(null);

    // Owner-set van de huidige scope, of de laatst ontvangen set als terugval; null zolang er niets is.
    readonly ownerClaims$$ = computed<IOwnerClaimsData | null>(() =>
        this.__ownerClaimsByScope$$()[this.__ownerScopeKey()] ?? this.__ownerClaimsLast$$()
    );

    private __ownerScopeKey(dGuid?: string | null): string {
        const __dGuid = dGuid !== undefined ? dGuid : (this.domainUserData?.[domainUserDto.guid] ?? null);
        return __dGuid ?? '';
    }

    // Alleen opslaan bij echte wijziging: anders zet elke response een nieuw object en herevalueert elke owner-check.
    ownerClaims_set(ownerClaims: IOwnerClaimsData | null | undefined, dGuid?: string | null): void {
        if (!ownerClaims) return;

        const __key = this.__ownerScopeKey(dGuid);
        if (_.isEqual(this.__ownerClaimsByScope$$()[__key], ownerClaims) && _.isEqual(this.__ownerClaimsLast$$(), ownerClaims)) return;

        // Apart van 'load [claims]': de owner-set staat los van userClaims (zie hierboven). Scope = dGuid,
        // leeg = app-niveau (geen domain). Gesorteerd op pageName/claim (alleen de log-kopie).
        const __sort = (claims: IUserClaimData[]) => _.sortBy(claims, [c => c.pageName?.toLowerCase(), c => c.claim?.toLowerCase()])
        LogService.envDev.logObject(`load [ownerClaims] scope [${__key || 'app'}]`, {
            recordOwner: __sort(ownerClaims.recordOwner ?? []),
            entityOwner: __sort(ownerClaims.entityOwner ?? []),
        })

        this.__ownerClaimsByScope$$.update(map => ({ ...map, [__key]: ownerClaims }));
        this.__ownerClaimsLast$$.set(ownerClaims);
    }

    // Combineert owner_check (is de ingelogde gebruiker de eigenaar van dít record — CreatedBy voor
    // 'record', zelf-identiteit voor 'entity') met de vraag of die eigenaarsrol de gevraagde claim ook
    // daadwerkelijk heeft (door een Administrator toegekend via AdminPanel > Functions/Claims).
    ownerClaim_check(kind: 'record' | 'entity', ownerGuid: string | null | undefined, pageName: string, claim: TClaimName): boolean {
        if (!this.owner_check(ownerGuid)) return false;

        const __claims = this.ownerClaims$$();
        if (!__claims) return false;

        const __list = kind === 'record' ? __claims.recordOwner : __claims.entityOwner;
        return __list.some(c =>
            c.pageName.toLowerCase() === pageName.toLowerCase()
            && c.claim.toLowerCase() === claim.toLowerCase()
        );
    }

    private __userClaims$$ = signal<IUserClaimData[]>([]);
    userClaims$$ = this.__userClaims$$.asReadonly();
    get UserClaims() { return this.__userClaims$$();}
    set UserClaims(value: IUserClaimData[]) { 
        this.__userClaims$$.set(value); 
    }

    private __claims_addLevelData(userClaims: IUserClaimData[], dGuid?: string | null, oGuid?: string | null): IUserClaimData[] {
        return userClaims.map(claim => {
            claim.dGuid = dGuid ?? null
            claim.oGuid = oGuid ?? null
            return claim
        })
    }

    claims_add(userClaims: IUserClaimData[], dGuid?: string | null, oGuid?: string | null) {
        if (!userClaims) {return}

        const _claims_new  = this.__claims_addLevelData(userClaims, dGuid, oGuid)
        const _claims_curr = this.UserClaims ?? []
        const _claims_diff = _.differenceWith(_claims_new, _claims_curr, _.isEqual)
        // Niets nieuws: niet opnieuw zetten. Anders herevalueert elke response alle claim-checks.
        if (_claims_diff.length === 0) {return}

        const _claims_sort = _.sortBy(_claims_curr.concat(_claims_diff), ['dGuid', 'oGuid', 'pageName', 'claim'])

        this.__claims_set(_claims_sort)
    }

    // dGuid/oGuid optioneel expliciet meegeven i.p.v. altijd van domainUserData/projectMemberData
    // af te lezen: die twee zijn gedeelde, sessie-brede velden (zie ResponseStateService.apply) die
    // een AANROEPER bewust NIET altijd wil laten overschrijven (bv. AdminPanel/details, dat de
    // domein-0-scope van de ingelogde admin draagt maar geen "huidig bezochte domain" is — zie
    // adminPanelModelResolver). Zonder expliciete override zou een andere, tegelijk gemounte pagina
    // (bv. Home, via ActivatedRouteService.routeKeys' eigen domainUserData-fallback) de globale
    // velden ná zo'n response ineens met díe domein-0-guid zien, terwijl haar eigen claims op
    // null/null staan — een tab-lijst die plotseling "niet geautoriseerd" lijkt.
    claims_new(userClaims: IUserClaimData[], dGuidOverride?: string | null, oGuidOverride?: string | null) {
        if (!userClaims) {return}

        const __dGuid = dGuidOverride !== undefined ? dGuidOverride : (this.domainUserData?.[domainUserDto.guid]   ?? null)
        const __oGuid = oGuidOverride !== undefined ? oGuidOverride : (this.projectMemberData?.[projectMemberDto.guid] ?? null)

        // Aanvullen (claims_add), niet vervangen: elk HTTP-antwoord roept dit aan (response-state.
        // service.ts), maar draagt lang niet altijd de VOLLEDIGE claim-set voor deze (dGuid,oGuid)-
        // scope — sommige endpoints geven backend-zijde bewust een beperkte claimsDto mee, gescopet
        // op de pagina's/tabs die dát endpoint nodig heeft (bv. RoleController.Details vraagt expliciet
        // maar een handvol pageNames/tabNames op via Claims_getDto). Een scope-brede vervanging liet
        // zo'n kleiner antwoord de rest van de al geladen claims voor diezelfde scope wegvagen —
        // AdminPanel verloor zijn Roles/Pages/Claims-tabs zodra de gebruiker naar een Role-detailpagina
        // navigeerde (nog dezelfde dGuid) en dat kleinere claims-antwoord binnenkwam, waardoor de
        // Roles-tab bij terugkeer niet meer teruggevonden werd. Puur aanvullen is hier de enige
        // correcte semantiek: afwezigheid in een deel-antwoord betekent niet "ingetrokken", enkel
        // "niet opgevraagd".
        this.claims_add(userClaims, __dGuid, __oGuid)
    }

    private __claims_set(claims: IUserClaimData[]): void {
        LogService.envDev.logObject('load [claims]', claims)

        this.UserClaims = claims
    }

    claims_reset(): void {
        this.__claims_set([])
    }

    claim_check(componentName: ClaimPage | null, routeKeyData: IRouteKeyData | null, claim: TClaimName | null): boolean;
    claim_check(componentName: ClaimPage | null, routeKeyData: IRouteKeyData | null, claim: TClaimName | null, userClaims: IUserClaimData[]): boolean;
    claim_check(componentName: ClaimPage | null, routeKeyData: IRouteKeyData | null, claim: TClaimName | null, userClaims?: IUserClaimData[]): boolean {
        if (!componentName || !claim) {return false}

        const __userClaims = userClaims ?? this.UserClaims;
        const __dGuid = routeKeyData?.dauGuid ?? null;
        const __oGuid = routeKeyData?.pamGuid ?? null;

        return __userClaims?.some(userClaim =>
            (userClaim.dGuid ?? null) == __dGuid
            && (userClaim.oGuid ?? null) == __oGuid
            && userClaim.pageName.toLowerCase() == componentName.toLowerCase()
            && userClaim.claim.toLowerCase() == claim.toLowerCase()
        ) ?? false
    }
}
